import { graphqlClient } from '@/lib/graphql-client';
import { useAuthStore } from '@/state/store/auth';
import {
  buildCountActiveDepartmentEmployeesQuery,
  buildCountDepartmentEmployeesQuery,
  buildGetDepartmentsLookupQuery,
  buildGetDepartmentsQuery,
} from '../graphql/queries';
import {
  DELETE_DEPARTMENT_MUTATION,
  INSERT_DEPARTMENT_MUTATION,
  UPDATE_DEPARTMENT_MUTATION,
} from '../graphql/mutations';
import {
  Department,
  DepartmentInsertInput,
  DepartmentsPagingResult,
  DepartmentUpdateInput,
  GetDepartmentsParams,
} from '../types/department.types';

export const getDepartments = async (
  params: GetDepartmentsParams
): Promise<{ Departments: DepartmentsPagingResult }> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  const result = (await graphqlClient.query({
    query: buildGetDepartmentsQuery(filter, pageNo, pageSize, order),
  })) as { Departments: DepartmentsPagingResult };
  const items = await Promise.all(
    result.Departments.items.map(async (department) => ({
      ...department,
      ActiveUsers: await countActiveDepartmentEmployees(department.ItemId),
    }))
  );
  return { Departments: { ...result.Departments, items } };
};

/** One count query per row of the current page — Department stores no employee count. */
const countActiveDepartmentEmployees = async (departmentId: string): Promise<number> => {
  const result = (await graphqlClient.query({
    query: buildCountActiveDepartmentEmployeesQuery(departmentId),
  })) as { Employees: { totalCount: number } };
  return result.Employees.totalCount;
};

/** Key is unique per organization (`OrganizationId + Key` index), but a pre-submit check gives the
 * form a field-level error instead of a raw server error, matching the mbmProjectExists convention. */
export const departmentKeyExists = async (key: string): Promise<boolean> => {
  const result = (await graphqlClient.query({
    query: buildGetDepartmentsLookupQuery({ Key: key.trim() }, 1, 1),
  })) as { Departments: { totalCount: number } };
  return result.Departments.totalCount > 0;
};

/** Every department of the signed-in organization, paged through in full — Department has no read
 * policy, so the organization filter is explicit. */
export const getAllOrganizationDepartments = async (): Promise<
  Pick<Department, 'ItemId' | 'Title' | 'Key'>[]
> => {
  const organizationId = useAuthStore.getState().selectedOrgId;
  const pageSize = 100;
  const departments: Pick<Department, 'ItemId' | 'Title' | 'Key'>[] = [];
  for (let pageNo = 1; ; pageNo++) {
    const result = (await graphqlClient.query({
      query: buildGetDepartmentsLookupQuery({ OrganizationId: organizationId }, pageNo, pageSize),
    })) as { Departments: { totalCount: number; items: Department[] } };
    departments.push(...result.Departments.items);
    if (!result.Departments.items.length || departments.length >= result.Departments.totalCount)
      return departments;
  }
};

/** The department Keys (= Head of Department role slugs) of each of these organizations, for the
 * role dialog to offer each organization only its own departments' roles. Department has no read
 * policy, so other organizations' rows are visible. */
export const getDepartmentKeysByOrganization = async (
  organizationIds: string[]
): Promise<Record<string, string[]>> => {
  const byOrganization: Record<string, string[]> = {};
  if (organizationIds.length === 0) return byOrganization;
  const pageSize = 200;
  for (let pageNo = 1, read = 0; ; pageNo++) {
    const result = (await graphqlClient.query({
      query: buildGetDepartmentsLookupQuery(
        { OrganizationId: { $in: organizationIds } },
        pageNo,
        pageSize
      ),
    })) as { Departments: { totalCount: number; items: Department[] } };
    for (const department of result.Departments.items) {
      const organizationId = department.OrganizationId ?? '';
      (byOrganization[organizationId] ??= []).push(department.Key);
    }
    read += result.Departments.items.length;
    if (!result.Departments.items.length || read >= result.Departments.totalCount)
      return byOrganization;
  }
};

/** How many people are still assigned to a department — a department in use can't be deleted,
 * since their profiles, filters and scope checks would point at a department that no longer exists. */
export const countDepartmentEmployees = async (departmentId: string): Promise<number> => {
  const result = (await graphqlClient.query({
    query: buildCountDepartmentEmployeesQuery(departmentId),
  })) as { Employees: { totalCount: number } };
  return result.Employees.totalCount;
};

export const createDepartment = async (input: DepartmentInsertInput) => {
  return graphqlClient.mutate({
    query: INSERT_DEPARTMENT_MUTATION,
    variables: { input },
  });
};

export const updateDepartment = async (params: {
  itemId: string;
  input: DepartmentUpdateInput;
}) => {
  const filter = JSON.stringify({ _id: params.itemId });
  return graphqlClient.mutate({
    query: UPDATE_DEPARTMENT_MUTATION,
    variables: { filter, input: params.input },
  });
};

export const deleteDepartment = async (itemId: string) => {
  const filter = JSON.stringify({ _id: itemId });
  return graphqlClient.mutate({
    query: DELETE_DEPARTMENT_MUTATION,
    variables: { filter, input: { isHardDelete: false } },
  });
};
